import { PageContainer } from '@ant-design/pro-components';
import { useRequest, history } from '@umijs/max';
import { Card, Tag, Button, Typography, Space, Spin, Empty, Alert, Segmented, theme } from 'antd';
import { CrownOutlined, CheckCircleFilled, CheckOutlined, InfoCircleFilled, CalendarOutlined, ThunderboltOutlined, StarOutlined } from '@ant-design/icons';
import React from 'react';
import { loginModal } from '@/components/LoginModal';
import { getPlans } from '@/services/payment';
import { createStyles } from 'antd-style';
import { normalizePlanList, flattenPlanTabs, normalizeCycleKey, type CurrentSubscriptionSummary } from '@/pages/payment/utils';
import { formatLocaleMessage as t, getAppLocale } from '@/utils/i18n';

const { Title, Text } = Typography;

const PLAN_ICON_MAP: Record<string, React.ComponentType<{ style?: React.CSSProperties }>> = {
  thunder: ThunderboltOutlined,
  crown: CrownOutlined,
  star: StarOutlined,
};

const SUBSCRIPTION_ASSET_MAP = {
  banner: require('@/assets/icons/plan-banner-bg.png'),
  basic: require('@/assets/icons/plan-icon-basic.png'),
  standard: require('@/assets/icons/plan-icon-standard.png'),
  enterprise: require('@/assets/icons/plan-icon-enterprise.png'),
} as const;

const useStyles = createStyles(({ token }) => ({
  page: {
    minHeight: 'calc(100vh - 56px)',
    background: token.colorBgLayout,
  },
  pageShell: {
    width: '100%',
    maxWidth: 1280,
    margin: '0 auto',
  },
  hero: {
    padding: '22px 24px 18px',
    marginBottom: 20,
    borderRadius: token.borderRadiusLG + 8,
    background: token.colorBgContainer,
    border: `1px solid ${token.colorBorderSecondary}`,
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
  },
  currentSubscriptionSummary: {
    marginTop: 4,
    marginInline: 'auto',
    maxWidth: 860,
    padding: '16px 20px',
    borderRadius: 16,
    background: `linear-gradient(180deg, ${token.colorBgContainer} 0%, ${token.colorPrimaryBg} 100%)`,
    border: `1px solid ${token.colorPrimaryBorder}`,
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
  },
  currentSubscriptionSummaryHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginBottom: 8,
    color: token.colorPrimary,
    fontSize: 13,
    fontWeight: 800,
    letterSpacing: '0.3px',
  },
  currentSubscriptionSummaryTitle: {
    textAlign: 'center',
    fontSize: 20,
    fontWeight: 900,
    color: token.colorTextHeading,
    lineHeight: 1.3,
  },
  currentSubscriptionSummaryDesc: {
    marginTop: 8,
    textAlign: 'center',
    fontSize: 14,
    color: token.colorTextSecondary,
  },
  tabToggleWrap: {
    display: 'flex',
    justifyContent: 'center',
    margin: '18px 0 18px 0',
  },
  tabToggle: {
    padding: 6,
    borderRadius: 999,
    backgroundColor: token.colorFillQuaternary,
    border: `1px solid ${token.colorBorderSecondary}`,
  },
  plansGrid: {
    display: 'grid',
    gap: 18,
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 320px))',
    justifyContent: 'center',
    alignItems: 'stretch',
    padding: '8px 0 18px 0',
    ['@media (max-width: 640px)']: {
      gridTemplateColumns: '1fr',
      justifyContent: 'stretch',
    },
  },
  planCard: {
    position: 'relative',
    borderRadius: 20,
    transition: 'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
    border: `1px solid ${token.colorBorderSecondary}`,
    backgroundColor: token.colorBgContainer,
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    cursor: 'pointer',
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: '0 8px 18px -12px rgba(15,23,42,0.12)',
    },
  },
  selectedCard: {
    borderColor: token.colorPrimary,
    boxShadow: `0 8px 20px -16px ${token.colorPrimaryBorder}`,
  },
  selectedBadge: {
    position: 'absolute',
    top: 12,
    left: 12,
    zIndex: 2,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 10px',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 800,
    background: token.colorPrimaryBg,
    color: token.colorPrimaryTextActive,
    border: `1px solid ${token.colorPrimaryBorder}`,
  },
  currentPlanBadge: {
    background: token.colorPrimary,
    color: '#fff',
    borderRadius: '0 0 0 16px',
    padding: '6px 16px',
    fontSize: 13,
    fontWeight: 700,
    position: 'absolute',
    top: 0,
    right: 0,
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    letterSpacing: '0.5px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  planHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    minHeight: 56,
    marginBottom: 4,
  },
  priceContainer: {
    textAlign: 'center',
    padding: '20px 0 18px 0',
    background: token.colorFillQuaternary,
    borderRadius: 16,
    margin: '14px 0 18px 0',
    border: `1px solid ${token.colorBorderSecondary}`,
  },
  price: {
    fontSize: 38,
    fontWeight: 800,
    color: token.colorTextHeading,
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'center',
  },
  currency: {
    fontSize: 20,
    marginRight: 4,
    fontWeight: 600,
  },
  cycle: {
    fontSize: 16,
    color: token.colorTextSecondary,
    marginLeft: 4,
    fontWeight: 400,
  },
  featureItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: 14,
    fontSize: 15,
  },
  checkIcon: {
    color: token.colorSuccess,
    fontSize: 18,
    marginTop: 2,
  },
  subscribeButton: {
    height: 52,
    borderRadius: 16,
    fontSize: 16,
    fontWeight: 800,
    marginTop: 10,
    border: 'none',
    boxShadow: '0 10px 20px -12px rgba(33, 102, 255, 0.35)',
    transition: 'transform 180ms ease, box-shadow 180ms ease, filter 180ms ease, background 180ms ease',
    '&.ant-btn-primary': {
      background: 'linear-gradient(-45deg, rgb(33, 102, 255) 0%, rgb(222, 32, 253) 99%)',
      borderColor: 'transparent',
      color: '#fff',
    },
    /**
     * 这里显式覆盖 antd primary/default 按钮的 hover/focus 默认色，
     * 避免订阅按钮在鼠标移入时被框架样式回退成白底蓝字，破坏设计稿的高亮连续性。
     */
    '&.ant-btn-primary:not(:disabled):hover, &.ant-btn-primary:not(:disabled):focus-visible, &:not(:disabled):hover, &:not(:disabled):focus-visible': {
      background: 'linear-gradient(-45deg, rgb(50, 114, 255) 0%, rgb(230, 56, 255) 99%)',
      borderColor: 'transparent',
      color: '#fff',
      filter: 'brightness(1.03) saturate(1.04)',
      transform: 'translateY(-1px)',
      boxShadow: '0 14px 26px -14px rgba(33, 102, 255, 0.45)',
    },
  },
  stickyBar: {
    position: 'sticky',
    bottom: 12,
    zIndex: 10,
    marginTop: 10,
    display: 'flex',
    justifyContent: 'center',
  },
  stickyBarInner: {
    maxWidth: 920,
    width: '100%',
    borderRadius: 16,
    border: `1px solid ${token.colorBorderSecondary}`,
    background: token.colorBgElevated,
    boxShadow: '0 8px 18px -14px rgba(15,23,42,0.16)',
    padding: '12px 14px',
    display: 'flex',
    gap: 12,
    alignItems: 'center',
    justifyContent: 'space-between',
    backdropFilter: 'saturate(140%) blur(10px)',
  },
  stickyTitle: {
    fontSize: 14,
    fontWeight: 800,
    color: token.colorText,
  },
  stickyMeta: {
    fontSize: 12,
    color: token.colorTextSecondary,
  },
  embeddedWorkbench: {
    padding: '0 8px 8px',
  },
  embeddedSurface: {
    borderRadius: token.borderRadiusLG + 14,
    border: `1px solid ${token.colorBorderSecondary}`,
    background: token.colorBgContainer,
    boxShadow: '0 12px 36px -30px rgba(15, 23, 42, 0.16)',
    padding: '22px 22px 24px',
    ['@media (max-width: 768px)']: {
      padding: '18px 14px 18px',
    },
  },
  embeddedTitle: {
    margin: '0 0 18px',
    textAlign: 'center',
    fontSize: 22,
    fontWeight: 900,
    letterSpacing: '-0.03em',
    color: token.colorTextHeading,
  },
  embeddedSummaryFrame: {
    padding: '16px 18px',
    borderRadius: token.borderRadiusLG + 8,
    border: `1px solid ${token.colorBorderSecondary}`,
    background: `color-mix(in srgb, ${token.colorBgContainer} 96%, ${token.colorFillQuaternary})`,
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.7)',
  },
  embeddedSummaryBanner: {
    position: 'relative',
    overflow: 'hidden',
    width: 'min(100%, 860px)',
    margin: '0 auto',
    padding: '24px 32px',
    borderRadius: token.borderRadiusLG + 8,
    backgroundImage: `linear-gradient(90deg, rgba(23, 66, 197, 0.96) 0%, rgba(71, 110, 236, 0.82) 32%, rgba(89, 208, 255, 0.42) 58%, rgba(188, 66, 246, 0.82) 100%), url("${SUBSCRIPTION_ASSET_MAP.banner}")`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    boxShadow: '0 18px 32px -24px rgba(67, 92, 255, 0.48)',
    ['@media (max-width: 768px)']: {
      padding: '20px 18px',
    },
  },
  embeddedSummaryLabel: {
    fontSize: 14,
    lineHeight: '20px',
    fontWeight: 800,
    color: 'rgba(255,255,255,0.94)',
    letterSpacing: '0.04em',
  },
  embeddedSummaryMeta: {
    marginTop: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    flexWrap: 'wrap',
    gap: 14,
    color: '#fff',
  },
  embeddedSummaryPlan: {
    fontSize: 18,
    lineHeight: '26px',
    fontWeight: 900,
  },
  embeddedSummaryDivider: {
    fontSize: 20,
    lineHeight: 1,
    fontWeight: 300,
    opacity: 0.78,
  },
  embeddedSummaryDesc: {
    fontSize: 16,
    lineHeight: '24px',
    fontWeight: 700,
    color: 'rgba(255,255,255,0.9)',
  },
  embeddedTabsWrap: {
    display: 'flex',
    justifyContent: 'center',
    margin: '24px 0 22px',
  },
  embeddedTabs: {
    padding: 6,
    borderRadius: token.borderRadiusLG + 4,
    background: `color-mix(in srgb, ${token.colorFillQuaternary} 70%, ${token.colorBgContainer})`,
    border: `1px solid ${token.colorBorderSecondary}`,
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.7)',

    '& .ant-segmented-item': {
      borderRadius: token.borderRadius,
      color: token.colorTextSecondary,
    },
    '& .ant-segmented-item-label': {
      minWidth: 84,
      padding: '10px 18px',
      fontSize: 15,
      lineHeight: '22px',
      fontWeight: 800,
    },
    '& .ant-segmented-item-selected': {
      background: token.colorPrimary,
      color: '#fff',
      boxShadow: '0 12px 24px -18px rgba(52, 98, 255, 0.55)',
    },
  },
  embeddedGrid: {
    display: 'grid',
    gap: 16,
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    alignItems: 'stretch',
    ['@media (max-width: 1200px)']: {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
    ['@media (max-width: 720px)']: {
      gridTemplateColumns: '1fr',
    },
  },
  embeddedPlanCard: {
    position: 'relative',
    display: 'grid',
    gridTemplateRows: 'minmax(84px, auto) auto auto minmax(0, 1fr)',
    minHeight: 438,
    padding: '22px 22px 24px',
    borderRadius: token.borderRadiusLG + 8,
    border: `1px solid ${token.colorBorderSecondary}`,
    background: token.colorBgContainer,
    boxShadow: '0 12px 30px -28px rgba(15, 23, 42, 0.2)',
    transition: 'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
    cursor: 'pointer',
    rowGap: 0,
    alignItems: 'start',
    ['@media (max-width: 768px)']: {
      minHeight: 0,
      gridTemplateRows: 'auto auto auto minmax(0, 1fr)',
    },
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 18px 36px -28px rgba(15, 23, 42, 0.24)',
    },
  },
  embeddedPlanCardSelected: {
    border: '1px solid transparent',
    background: `
      linear-gradient(${token.colorBgContainer}, ${token.colorBgContainer}) padding-box,
      linear-gradient(145deg, rgba(52, 98, 255, 0.95), rgba(214, 40, 255, 0.72)) border-box
    `,
    boxShadow: '0 22px 40px -30px rgba(88, 96, 255, 0.34)',
  },
  embeddedPlanCardPromo: {
    border: '1px solid transparent',
    background: `
      linear-gradient(${token.colorBgContainer}, ${token.colorBgContainer}) padding-box,
      linear-gradient(145deg, rgba(55, 101, 255, 1), rgba(227, 43, 255, 1)) border-box
    `,
    boxShadow: '0 22px 42px -30px rgba(132, 72, 255, 0.4)',
  },
  embeddedPlanIcon: {
    width: 18,
    height: 20,
    objectFit: 'contain',
    flexShrink: 0,
  },
  embeddedPlanHeaderBlock: {
    display: 'grid',
    gridTemplateRows: '20px minmax(28px, auto) auto',
    rowGap: 8,
    minHeight: 92,
    ['@media (max-width: 1200px)']: {
      minHeight: 96,
    },
    ['@media (max-width: 720px)']: {
      gridTemplateRows: '20px auto auto',
      minHeight: 'unset',
    },
  },
  embeddedPlanIconRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-end',
    minHeight: 20,
  },
  embeddedPlanTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    minWidth: 0,
    flexWrap: 'wrap',
  },
  embeddedPlanTitleMain: {
    minWidth: 0,
    flex: '0 1 auto',
  },
  embeddedPlanName: {
    fontSize: 20,
    lineHeight: '28px',
    fontWeight: 900,
    color: token.colorTextHeading,
    minWidth: 0,
    flex: '0 1 auto',
  },
  embeddedPlanBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
    height: 24,
    padding: '0 9px',
    borderRadius: 8,
    fontSize: 11,
    lineHeight: '16px',
    fontWeight: 700,
    color: token.colorPrimary,
    background: `color-mix(in srgb, ${token.colorPrimaryBg} 88%, ${token.colorBgContainer})`,
    whiteSpace: 'nowrap',
  },
  embeddedPlanBadgePromo: {
    color: '#fff',
    background: 'linear-gradient(90deg, #3D66F5 0%, #D628FF 100%)',
  },
  embeddedPlanBadgeValue: {
    color: '#3566F5',
    background: 'linear-gradient(90deg, rgba(232, 241, 255, 0.98) 0%, rgba(241, 246, 255, 0.98) 32%, rgba(250, 236, 255, 0.98) 100%)',
    boxShadow: 'inset 0 0 0 1px rgba(116, 120, 255, 0.08)',
  },
  embeddedPlanSubtitle: {
    marginTop: -10,
    minHeight: 22,
    fontSize: 14,
    lineHeight: '22px',
    color: token.colorTextSecondary,
    ['@media (max-width: 720px)']: {
      minHeight: 'unset',
    },
  },
  embeddedPriceBox: {
    margin: '22px 0 20px',
    borderRadius: token.borderRadiusLG + 2,
    border: `1px solid ${token.colorBorderSecondary}`,
    background: `color-mix(in srgb, ${token.colorFillQuaternary} 78%, ${token.colorBgContainer})`,
    padding: '22px 16px',
    textAlign: 'center',
  },
  embeddedPriceRow: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'center',
    gap: 4,
    color: token.colorTextHeading,
  },
  embeddedPriceCurrency: {
    fontSize: 18,
    lineHeight: 1,
    fontWeight: 700,
  },
  embeddedPriceValue: {
    fontSize: 38,
    lineHeight: 1,
    fontWeight: 900,
    letterSpacing: '-0.04em',
  },
  embeddedPriceCycle: {
    fontSize: 14,
    lineHeight: '20px',
    fontWeight: 700,
    color: token.colorTextSecondary,
  },
  embeddedCta: {
    height: 50,
    borderRadius: token.borderRadiusLG + 2,
    border: 'none',
    fontSize: 16,
    fontWeight: 800,
    background: '#0d0d0d',
    color: '#fff',
    boxShadow: 'none',
    transition: 'transform 180ms ease, box-shadow 180ms ease, opacity 180ms ease, filter 180ms ease, background 180ms ease',
    '&.ant-btn-default': {
      background: '#0d0d0d',
      borderColor: 'transparent',
      color: '#fff',
    },
    /**
     * 弹窗里的黑色订阅按钮在静止态保持黑色，
     * 但按照设计稿，hover/focus 时要切换成高亮订阅按钮同款蓝紫渐变，
     * 既挡住 antd 默认的白底蓝字，也让交互反馈足够明确。
     */
    '&.ant-btn-default:not(:disabled):hover, &.ant-btn-default:not(:disabled):focus-visible, &:not(:disabled):hover, &:not(:disabled):focus-visible': {
      background: 'linear-gradient(90deg, #4473FF 0%, #E243FF 100%)',
      borderColor: 'transparent',
      color: '#fff',
      filter: 'brightness(1.03) saturate(1.04)',
      transform: 'translateY(-1px)',
      boxShadow: '0 20px 30px -22px rgba(116, 73, 255, 0.62)',
    },
  },
  embeddedCtaPromo: {
    background: 'linear-gradient(90deg, #2F63F5 0%, #D628FF 100%)',
    boxShadow: '0 20px 30px -26px rgba(116, 73, 255, 0.56)',
    color: '#fff',
    '&.ant-btn-default': {
      background: 'linear-gradient(90deg, #2F63F5 0%, #D628FF 100%)',
      borderColor: 'transparent',
      color: '#fff',
    },
    '&.ant-btn-default:not(:disabled):hover, &.ant-btn-default:not(:disabled):focus-visible, &:not(:disabled):hover, &:not(:disabled):focus-visible': {
      background: 'linear-gradient(90deg, #4473FF 0%, #E243FF 100%)',
      borderColor: 'transparent',
      color: '#fff',
      filter: 'brightness(1.03) saturate(1.04)',
      boxShadow: '0 20px 30px -22px rgba(116, 73, 255, 0.62)',
    },
  },
  embeddedCtaSelected: {
    background: 'linear-gradient(90deg, #2F63F5 0%, #D628FF 100%)',
    boxShadow: '0 20px 30px -26px rgba(116, 73, 255, 0.56)',
    color: '#fff',
    '&.ant-btn-default': {
      background: 'linear-gradient(90deg, #2F63F5 0%, #D628FF 100%)',
      borderColor: 'transparent',
      color: '#fff',
    },
    '&.ant-btn-default:not(:disabled):hover, &.ant-btn-default:not(:disabled):focus-visible, &:not(:disabled):hover, &:not(:disabled):focus-visible': {
      background: 'linear-gradient(90deg, #4473FF 0%, #E243FF 100%)',
      borderColor: 'transparent',
      color: '#fff',
      filter: 'brightness(1.03) saturate(1.04)',
      boxShadow: '0 20px 30px -22px rgba(116, 73, 255, 0.62)',
    },
  },
  embeddedFeatureList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    marginTop: 18,
  },
  embeddedFeatureItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    fontSize: 15,
    lineHeight: '24px',
    color: token.colorText,
  },
  embeddedFeatureIcon: {
    marginTop: 5,
    fontSize: 12,
    color: token.colorText,
  },
  embeddedErrorWrap: {
    marginTop: 18,
  },
  embeddedEmptyWrap: {
    padding: '28px 0 8px',
  },
}));

type Plan = ReturnType<typeof flattenPlanTabs>[number];

const getBillingCycleLabel = (billingCycle?: string) => {
  const key = String(billingCycle || '').toUpperCase();
  if (key === 'PERMANENT') return '';
  if (key === 'MONTHLY') return t('pages.payment.plans.billingCycle.month', 'month');
  if (key === 'YEARLY' || key === 'ANNUAL' || key === 'ANNUALLY') return t('pages.payment.plans.billingCycle.year', 'year');
  if (key === 'TWO_WEEK' || key === 'TWO_WEEKS' || key === 'BIWEEKLY') return t('pages.payment.plans.billingCycle.twoWeek', '2 weeks');
  if (key === 'QUARTERLY') return t('pages.payment.plans.billingCycle.quarter', 'quarter');
  return billingCycle || t('pages.payment.plans.billingCycle.generic', 'cycle');
};

const getBillingCycleTabLabel = (billingCycle?: string) => {
  const key = String(billingCycle || '').toUpperCase();
  if (key === 'MONTHLY') return t('pages.payment.plans.billingTab.month', 'Monthly');
  if (key === 'YEARLY' || key === 'ANNUAL' || key === 'ANNUALLY') return t('pages.payment.plans.billingTab.year', 'Yearly');
  if (key === 'TWO_WEEK' || key === 'TWO_WEEKS' || key === 'BIWEEKLY') return t('pages.payment.plans.billingTab.twoWeek', 'Promo');
  if (key === 'QUARTERLY') return t('pages.payment.plans.billingTab.quarter', 'Quarterly');
  if (key === 'SPECIAL_OFFER') return t('pages.payment.plans.billingTab.specialOffer', 'Special');
  return billingCycle || t('pages.payment.plans.billingTab.generic', 'Plans');
};

function isPromoCycle(value?: string) {
  const normalized = normalizeCycleKey(value);
  const raw = String(value || '').toLowerCase();
  return normalized === 'TWO_WEEK'
    || raw.includes('promo')
    || raw.includes('special')
    || raw.includes('trial');
}

/**
 * 设计稿里“两周特惠”需要在月付/年付视图下持续可见，
 * 所以这里把促销套餐从常规账单周期里单独识别出来，供 embedded 弹窗合并展示。
 */
function isPromoPlan(plan: Pick<Plan, 'billingCycle' | 'planCode' | 'planCategory'>) {
  const code = `${String(plan.planCode || '')} ${String(plan.planCategory || '')}`.toLowerCase();
  return isPromoCycle(plan.billingCycle)
    || code.includes('promo')
    || code.includes('special')
    || code.includes('trial');
}

function dedupePlansByCode(source: Plan[]) {
  const seen = new Set<string>();
  return source.filter((plan) => {
    const key = String(plan.planCode || plan.id || '');
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

function getEmbeddedPlanIcon(plan: Plan) {
  const code = String(plan.planCode || '').toLowerCase();
  if (code.includes('basic')) return SUBSCRIPTION_ASSET_MAP.basic;
  if (code.includes('standard') || code === 'pro') return SUBSCRIPTION_ASSET_MAP.standard;
  if (code.includes('enterprise') || code.includes('team') || code.includes('business')) return SUBSCRIPTION_ASSET_MAP.enterprise;
  return undefined;
}

function isYearlyValueBadge(plan: Pick<Plan, 'billingCycle'>, badgeText?: string) {
  if (!badgeText) {
    return false;
  }

  const cycle = normalizeCycleKey(plan.billingCycle);
  return cycle === 'YEARLY' || badgeText === t('pages.payment.plans.badge.year', 'Better value for long-term use');
}

const isFreePrice = (price: number | string) => {
  const numericPrice = Number(price || 0);
  return !Number.isFinite(numericPrice) || numericPrice <= 0;
};

const formatPriceValue = (price: number | string) => {
  const numericPrice = Number(price || 0);
  if (!Number.isFinite(numericPrice)) {
    return '0';
  }
  if (numericPrice <= 0) {
    return t('pages.payment.plans.free', 'Free');
  }
  return numericPrice.toFixed(numericPrice % 1 === 0 ? 0 : 2);
};

const formatQuota = (quota: number | string) => {
  const n = Number(quota || 0);
  return (Number.isFinite(n) ? n : 0).toFixed(0);
};

const getRolloverText = (plan: Plan) => {
  if (plan.rolloverPolicy === 'FULL') {
    return t('pages.payment.plans.rollover.full', 'Full quota rollover');
  }
  if (plan.rolloverPolicy === 'RATIO' && plan.rolloverRatio != null) {
    return t('pages.payment.plans.rollover.ratio', '{ratio}% quota rollover', {
      ratio: Math.round(Number(plan.rolloverRatio) * 100),
    });
  }
  return t('pages.payment.plans.rollover.none', 'No quota rollover');
};

const getPlanSubtitle = (plan: Plan) => {
  const custom = String(plan.displaySubtitle || '').trim();
  if (custom) return custom;

  const code = String(plan.planCode || '').toLowerCase();
  if (code === 'free') return t('pages.payment.plans.subtitle.free', 'Best for first-time exploration');
  if (code === 'special_offer') return t('pages.payment.plans.subtitle.specialOffer', 'Great for short trials and quick onboarding');
  if (code.includes('basic')) return t('pages.payment.plans.subtitle.basic', 'Best for light daily use');
  if (code.includes('standard') || code === 'pro') return t('pages.payment.plans.subtitle.standard', 'Best for stable high-frequency use');
  if (code.includes('enterprise')) return t('pages.payment.plans.subtitle.enterprise', 'Best for teams and larger quotas');
  return t('pages.payment.plans.subtitle.default', 'Choose based on your current usage scale');
};

const getDisplayBadge = (plan: Plan) => {
  const custom = String(plan.displayBadge || '').trim();
  if (custom) return custom;
  const cycle = normalizeCycleKey(plan.billingCycle);
  if (cycle === 'TWO_WEEK') return t('pages.payment.plans.badge.twoWeek', 'Limited-time trial');
  if (cycle === 'YEARLY') return t('pages.payment.plans.badge.year', 'Better value for long-term use');
  return '';
};

const getPlanFeatures = (plan: Plan) => {
  if (Array.isArray(plan.displayFeatures) && plan.displayFeatures.length > 0) {
    return plan.displayFeatures.filter(Boolean).slice(0, 4);
  }

  const features = [
    t('pages.payment.plans.feature.credits', 'Includes {credits} credits', {
      credits: formatQuota(plan.creditQuota),
    }),
    t('pages.payment.plans.feature.duration', 'Valid for {days} days', {
      days: Number(plan.durationDays || 0) || 0,
    }),
    getRolloverText(plan),
  ];
  return features.filter(Boolean).slice(0, 4);
};

/**
 * 套餐列表既会出现在受保护的支付页面，也会出现在游客可直接打开的全局订阅弹窗里。
 * 因此“立刻订阅”不能只依赖路由级登录拦截；真正进入 checkout 前还需要再看一次本地 token，
 * 避免游客直接推进到支付确认态，打断“先登录/注册，再购买”的链路。
 */
const hasClientLoginToken = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  return Boolean(localStorage.getItem('token'));
};

const formatSubscriptionExpiry = (value?: string) => {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const buildSubscriptionSummary = (currentSub?: CurrentSubscriptionSummary) => {
  const daysRemaining = Number(currentSub?.daysRemaining);
  const hasSubscription = currentSub?.hasSubscription === true
    || Boolean(currentSub?.planName)
    || Boolean(currentSub?.planCode)
    || Boolean(currentSub?.currentPeriodEnd)
    || Number.isFinite(daysRemaining);

  if (!hasSubscription) {
    return null;
  }

  // daysRemaining 为 null/undefined 且无到期时间 → 永久订阅
  const isPermanent = currentSub?.daysRemaining == null && !currentSub?.currentPeriodEnd;

  const safeDays = Math.max(0, Number.isFinite(daysRemaining) ? daysRemaining : 0);
  const planName = String(
    currentSub?.planName
    || currentSub?.planCode
    || t('pages.payment.plans.summary.currentPlan', 'Current Plan'),
  );
  const expiryText = formatSubscriptionExpiry(currentSub?.currentPeriodEnd);

  if (isPermanent) {
    return {
      title: planName,
      description: t('pages.payment.plans.summary.permanent', 'Permanently active'),
    };
  }

  const remainingLabel = Number.isFinite(daysRemaining)
    ? t('pages.payment.plans.summary.remaining', '{days} days remaining', { days: safeDays })
    : '';
  const remainingText = remainingLabel
    ? `${getAppLocale() === 'zh-CN' ? '（' : ' ('}${remainingLabel}${getAppLocale() === 'zh-CN' ? '）' : ')'}`
    : '';

  return {
    title: planName,
    description: expiryText
      ? t('pages.payment.plans.summary.validUntil', 'Active until {date}{remaining}', {
        date: expiryText,
        remaining: remainingText,
      })
      : remainingLabel,
  };
};

type StartCheckoutContext =
  | { orderType: 'SUBSCRIPTION'; planCode: string; planName?: string; billingCycle: string; durationDays?: number }
  | { orderType: 'RECHARGE'; rechargeAmount: number; currency?: string; paymentMethod?: string };

const Plans: React.FC<{
  embedded?: boolean;
  onStartCheckout?: (ctx: StartCheckoutContext) => void;
}> = ({ embedded, onStartCheckout }) => {
  const { styles } = useStyles();
  const { token } = theme.useToken();

  const {
    data: planList,
    loading,
    error,
  } = useRequest(getPlans, {
    // 套餐页只消费后端返回的展示/购买字段，不再自行推导订阅规则。
    formatResult: normalizePlanList,
    cacheKey: 'payment-plans',
    staleTime: 15_000,
    onError: (e) => {
      console.error('Failed to load plans:', e);
    },
  });
  const tabs = React.useMemo(() => Array.isArray(planList?.tabs) ? planList.tabs : [], [planList?.tabs]);
  const plans = React.useMemo(() => flattenPlanTabs(tabs), [tabs]);
  const visibleTabs = React.useMemo(() => {
    if (!embedded) {
      return tabs;
    }

    const billingTabs = tabs.filter((tab) => !isPromoCycle(tab.tabKey) && !isPromoCycle(tab.planCategory));
    return billingTabs.length > 0 ? billingTabs : tabs;
  }, [embedded, tabs]);
  const currentSub = React.useMemo(
    () => planList?.currentSubscription,
    [planList?.currentSubscription],
  );
  /**
   * “两周特惠”的强强调样式只应该服务于未订阅用户的引导。
   * 一旦用户已经存在有效付费订阅，就要把视觉主次让给当前套餐卡片，
   * 否则会出现“明明当前是标准会员，却看起来像是两周特惠被选中”的误导。
   */
  const hasActivePaidSubscription = React.useMemo(
    () => planList?.paidSubscriptionActive === true || currentSub?.paidSubscriptionActive === true,
    [planList?.paidSubscriptionActive, currentSub?.paidSubscriptionActive],
  );
  const currentSubscriptionSummary = React.useMemo(
    () => buildSubscriptionSummary(currentSub),
    [currentSub],
  );
  const [activeTabKey, setActiveTabKey] = React.useState<string>('');

  React.useEffect(() => {
    if (visibleTabs.length === 0) return;
    if (visibleTabs.some((tab) => tab.tabKey === activeTabKey)) return;
    const defaultTabKey = String(planList?.defaultTabKey || '');
    const matched = visibleTabs.find((tab) => tab.tabKey === defaultTabKey);
    setActiveTabKey(matched?.tabKey || visibleTabs[0].tabKey);
  }, [visibleTabs, activeTabKey, planList?.defaultTabKey]);

  const [selectedPlanCode, setSelectedPlanCode] = React.useState<string>('');

  // Clear plan selection when switching billing-cycle tabs to avoid
  // carrying a stale match (same planCode, different cycle) across tabs.
  React.useEffect(() => {
    setSelectedPlanCode('');
  }, [activeTabKey]);

  const activeTab = React.useMemo(
    () => visibleTabs.find((tab) => tab.tabKey === activeTabKey) || visibleTabs[0],
    [visibleTabs, activeTabKey],
  );
  const cyclePlans = React.useMemo(() => activeTab?.plans || [], [activeTab]);
  const displayedPlans = React.useMemo(() => {
    if (!embedded) {
      return cyclePlans;
    }

    const promoPlans = dedupePlansByCode(plans.filter((plan) => isPromoPlan(plan)));
    const billingPlans = dedupePlansByCode(cyclePlans.filter((plan) => !isPromoPlan(plan)));
    return dedupePlansByCode([...promoPlans, ...billingPlans]);
  }, [embedded, plans, cyclePlans]);
  const selectablePlans = React.useMemo(() => (embedded ? displayedPlans : cyclePlans), [embedded, displayedPlans, cyclePlans]);

  React.useEffect(() => {
    const exists = selectablePlans.some(
      (p) => String(p?.planCode || '').toLowerCase() === String(selectedPlanCode || '').toLowerCase(),
    );
    if (!exists && selectedPlanCode) {
      setSelectedPlanCode('');
    }
  }, [selectedPlanCode, selectablePlans]);

  React.useEffect(() => {
    if (!embedded || selectedPlanCode || selectablePlans.length === 0) {
      return;
    }

    // Only auto-select the plan the backend explicitly marks as the current
    // subscription (plan.current). Matching by planCode alone would incorrectly
    // highlight the same-named plan in a different billing cycle (e.g. yearly
    // "standard" when the user actually subscribes to monthly "standard").
    const defaultPlan = selectablePlans.find((plan) => plan.current);
    if (defaultPlan?.planCode) {
      setSelectedPlanCode(defaultPlan.planCode);
    }
  }, [embedded, selectablePlans, selectedPlanCode]);

  const selectedPlan = React.useMemo(() => {
    const code = String(selectedPlanCode || '');
    return selectablePlans.find((p) => String(p?.planCode || '').toLowerCase() === code.toLowerCase());
  }, [selectablePlans, selectedPlanCode]);

  const selectedPlanIsFree = React.useMemo(() => {
    const n = Number(selectedPlan?.price ?? 0);
    return !Number.isFinite(n) || n <= 0;
  }, [selectedPlan?.price]);

  const handleSubscribe = (plan: Plan) => {
    if (!hasClientLoginToken()) {
      loginModal.show();
      return;
    }

    if (embedded && onStartCheckout) {
      onStartCheckout({
        orderType: 'SUBSCRIPTION',
        planCode: String(plan.planCode || ''),
        planName: String(plan.planName || ''),
        billingCycle: String(plan.billingCycle || ''),
        durationDays: Number(plan.durationDays || 0) || undefined,
      });
      return;
    }

    const params = new URLSearchParams();
    params.set('orderType', 'SUBSCRIPTION');
    params.set('planCode', String(plan.planCode || ''));
    params.set('billingCycle', String(plan.billingCycle || ''));
    history.push(`/payment/checkout?${params.toString()}`);
  };

  if (loading) {
    if (embedded) {
      return (
        <div className={styles.pageShell} style={{ textAlign: 'center', padding: 100 }}>
          <Spin size="large" />
        </div>
      );
    }

    return (
      <PageContainer
        className={styles.page}
        ghost={false}
        title={t('pages.payment.plans.title', 'Choose a subscription plan')}
        subTitle={t('pages.payment.plans.pageSubtitle', 'Compare plan benefits and choose the subscription that matches your current usage scale.')}
      >
        <div className={styles.pageShell} style={{ textAlign: 'center', padding: 100 }}>
          <Spin size="large" />
        </div>
      </PageContainer>
    );
  }

  if (embedded) {
    return (
      <div className={styles.embeddedWorkbench}>
        <div className={styles.embeddedSurface}>
          <Title level={3} className={styles.embeddedTitle}>
            {t('pages.payment.plans.title', 'Choose a subscription plan')}
          </Title>

          {currentSubscriptionSummary && (
            <div className={styles.embeddedSummaryFrame}>
              <div className={styles.embeddedSummaryBanner}>
                <div className={styles.embeddedSummaryLabel}>
                  {t('pages.payment.plans.summary.active', 'Current active subscription')}
                </div>
                <div className={styles.embeddedSummaryMeta}>
                  <span className={styles.embeddedSummaryPlan}>{currentSubscriptionSummary.title}</span>
                  {currentSubscriptionSummary.description && (
                    <>
                      <span className={styles.embeddedSummaryDivider}>|</span>
                      <span className={styles.embeddedSummaryDesc}>{currentSubscriptionSummary.description}</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className={styles.embeddedErrorWrap}>
              <Alert
                type="error"
                showIcon
                message={t('pages.payment.plans.loadFailed', 'Failed to load subscription plans')}
                description={(error as any)?.message || t('pages.payment.plans.loadFailedDesc', 'Please refresh and try again later, or ask an administrator to check the payment plan configuration.')}
              />
            </div>
          )}

          {!error && visibleTabs.length > 1 && (
            <div className={styles.embeddedTabsWrap}>
              <Segmented
                size="large"
                className={styles.embeddedTabs}
                value={activeTab?.tabKey}
                onChange={(v) => setActiveTabKey(String(v))}
                options={visibleTabs.map((tab) => ({
                  label: tab.tabName || getBillingCycleTabLabel(tab.tabKey),
                  value: tab.tabKey,
                }))}
              />
            </div>
          )}

          {!error && plans.length === 0 && (
            <div className={styles.embeddedEmptyWrap}>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t('pages.payment.plans.empty', 'No subscription plans are available right now. Please contact an administrator.')}
              />
            </div>
          )}

          {!error && plans.length > 0 && selectablePlans.length === 0 && (
            <div className={styles.embeddedEmptyWrap}>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={activeTab?.tabName ? t('pages.payment.plans.emptyGroup', 'No {group} plans are available right now', { group: activeTab.tabName }) : t('pages.payment.plans.empty', 'No subscription plans are available right now. Please contact an administrator.')}
              />
            </div>
          )}

          {!error && selectablePlans.length > 0 && (
            <div className={styles.embeddedGrid}>
              {selectablePlans.map((plan) => {
                const purchaseLimit = Number((plan as any).purchaseLimit);
                const isSelected = String(selectedPlanCode || '').toLowerCase() === String(plan.planCode || '').toLowerCase();
                const isPromoCard = isPromoPlan(plan);
                const shouldEmphasizePromo = isPromoCard && !hasActivePaidSubscription;
                const actionDisabled = Boolean(plan.actionDisabled);
                const actionLabel = String(
                  plan.actionLabel ||
                  (Boolean(plan.current)
                    ? t('pages.payment.plans.currentSubscription', 'Current Plan')
                    : actionDisabled
                      ? t('pages.payment.plans.cta.subscribed', 'Subscribed to another plan')
                      : isFreePrice(plan.price)
                        ? t('pages.payment.plans.cta.tryNow', 'Try Now')
                        : t('pages.payment.plans.cta.subscribeNow', 'Subscribe Now')),
                );
                const badgeText = getDisplayBadge(plan);
                const iconSrc = isPromoCard ? undefined : getEmbeddedPlanIcon(plan);
                const cardClassName = `${styles.embeddedPlanCard} ${shouldEmphasizePromo ? styles.embeddedPlanCardPromo : ''} ${isSelected ? styles.embeddedPlanCardSelected : ''}`.trim();
                const buttonClassName = `${styles.embeddedCta} ${shouldEmphasizePromo ? styles.embeddedCtaPromo : ''} ${isSelected ? styles.embeddedCtaSelected : ''}`.trim();
                const badgeClassName = `${styles.embeddedPlanBadge} ${isPromoCard ? styles.embeddedPlanBadgePromo : ''} ${isYearlyValueBadge(plan, badgeText) ? styles.embeddedPlanBadgeValue : ''}`.trim();

                return (
                  <div
                    key={`${activeTab?.tabKey || 'tab'}-${plan.id}`}
                    className={cardClassName}
                    role="button"
                    aria-pressed={isSelected}
                    onClick={() => setSelectedPlanCode(plan.planCode)}
                  >
                    <div className={styles.embeddedPlanHeaderBlock}>
                      <div className={styles.embeddedPlanIconRow}>
                        {iconSrc && <img src={iconSrc} alt="" className={styles.embeddedPlanIcon} />}
                      </div>

                      <div className={styles.embeddedPlanTitleRow}>
                        <div className={styles.embeddedPlanTitleMain}>
                          <div className={styles.embeddedPlanName}>{plan.planName}</div>
                        </div>
                        {badgeText && <span className={badgeClassName}>{badgeText}</span>}
                      </div>

                      <div className={styles.embeddedPlanSubtitle}>{getPlanSubtitle(plan)}</div>
                    </div>

                    <div className={styles.embeddedPriceBox}>
                      <div className={styles.embeddedPriceRow}>
                        {isFreePrice(plan.price) ? (
                          <span className={styles.embeddedPriceValue}>{t('pages.payment.plans.free', 'Free')}</span>
                        ) : (
                          <>
                            <span className={styles.embeddedPriceCurrency}>¥</span>
                            <span className={styles.embeddedPriceValue}>{formatPriceValue(plan.price)}</span>
                            {getBillingCycleLabel(plan.billingCycle) && (
                              <span className={styles.embeddedPriceCycle}>/ {getBillingCycleLabel(plan.billingCycle)}</span>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    <Button
                      type="default"
                      block
                      className={buttonClassName}
                      disabled={actionDisabled}
                      onClick={(event) => {
                        event.stopPropagation();
                        setSelectedPlanCode(plan.planCode);
                        handleSubscribe(plan);
                      }}
                    >
                      {actionLabel}
                    </Button>

                    <div className={styles.embeddedFeatureList}>
                      {getPlanFeatures(plan).map((feature, index) => (
                        <div className={styles.embeddedFeatureItem} key={`${plan.id}-embedded-feature-${index}`}>
                          <CheckOutlined className={styles.embeddedFeatureIcon} />
                          <span>{feature}</span>
                        </div>
                      ))}
                      {Number.isFinite(purchaseLimit) && purchaseLimit > 0 && (
                        <div className={styles.embeddedFeatureItem}>
                          <InfoCircleFilled className={styles.embeddedFeatureIcon} />
                          <span>{t('pages.payment.plans.purchaseLimit', 'Purchase limit: {count} per user', { count: purchaseLimit })}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  const content = (
    <div className={styles.pageShell} style={{ padding: embedded ? 0 : '12px 0 0' }}>
      <div className={styles.hero}>
        {embedded && (
          <Title level={3} style={{ textAlign: 'center', marginBottom: 6 }}>
            {t('pages.payment.plans.title', 'Choose a subscription plan')}
          </Title>
        )}
        {currentSubscriptionSummary && (
          <div className={styles.currentSubscriptionSummary}>
            <div className={styles.currentSubscriptionSummaryHeader}>
              <CalendarOutlined />
              <span>{t('pages.payment.plans.summary.active', 'Current active subscription')}</span>
            </div>
            <div className={styles.currentSubscriptionSummaryTitle}>
              {currentSubscriptionSummary.title}
            </div>
            {currentSubscriptionSummary.description && (
              <div className={styles.currentSubscriptionSummaryDesc}>
                {currentSubscriptionSummary.description}
              </div>
            )}
          </div>
        )}
      </div>

      {error && (
        <Alert
          type="error"
          showIcon
          message={t('pages.payment.plans.loadFailed', 'Failed to load subscription plans')}
          description={(error as any)?.message || t('pages.payment.plans.loadFailedDesc', 'Please refresh and try again later, or ask an administrator to check the payment plan configuration.')}
          style={{ marginBottom: 24 }}
        />
      )}

      {!error && plans.length === 0 && (
        <Card>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t('pages.payment.plans.empty', 'No subscription plans are available right now. Please contact an administrator.')}
          />
        </Card>
      )}

      {!error && plans.length > 0 && (
        <>
          {visibleTabs.length > 1 && (
            <div className={styles.tabToggleWrap}>
              <Segmented
                size="large"
                className={styles.tabToggle}
                value={activeTab?.tabKey}
                onChange={(v) => setActiveTabKey(String(v))}
                options={visibleTabs.map((tab) => ({
                  label: tab.tabName || getBillingCycleTabLabel(tab.tabKey),
                  value: tab.tabKey,
                }))}
              />
            </div>
          )}

          {cyclePlans.length === 0 ? (
            <Card>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={activeTab?.tabName ? t('pages.payment.plans.emptyGroup', 'No {group} plans are available right now', { group: activeTab.tabName }) : t('pages.payment.plans.empty', 'No subscription plans are available right now. Please contact an administrator.')}
              />
            </Card>
          ) : (
            <div
              className={styles.plansGrid}
              style={cyclePlans.length === 1 ? { gridTemplateColumns: 'min(520px, 1fr)' } : undefined}
            >
              {cyclePlans.map((plan) => {
                const purchaseLimit = Number((plan as any).purchaseLimit);
                const isSelected =
                  String(selectedPlanCode || '').toLowerCase() === String(plan.planCode || '').toLowerCase();
                const isCurrentVariant = Boolean(plan.current);
                const actionDisabled = Boolean(plan.actionDisabled);
                const actionLabel = String(
                  plan.actionLabel ||
                  (isCurrentVariant
                    ? t('pages.payment.plans.currentSubscription', 'Current Plan')
                    : actionDisabled
                      ? t('pages.payment.plans.cta.subscribed', 'Subscribed to another plan')
                      : isFreePrice(plan.price)
                        ? t('pages.payment.plans.cta.tryNow', 'Try Now')
                        : t('pages.payment.plans.cta.subscribeNow', 'Subscribe Now')),
                );

                return (
                  <Card
                    key={`${activeTab?.tabKey || 'tab'}-${plan.id}`}
                    className={`${styles.planCard} ${isSelected ? styles.selectedCard : ''}`}
                    onClick={() => {
                      setSelectedPlanCode(plan.planCode);
                    }}
                    hoverable
                    role="button"
                    aria-pressed={isSelected}
                  >
                    {isSelected && (
                      <div className={styles.selectedBadge}>
                        <CheckCircleFilled />
                        {t('pages.payment.plans.selected', 'Selected')}
                      </div>
                    )}
                    {isCurrentVariant && (
                      <div className={styles.currentPlanBadge}>
                        <CheckCircleFilled style={{ marginRight: 4 }} />
                        {t('pages.payment.plans.currentSubscription', 'Current Plan')}
                      </div>
                    )}

                    <Space direction="vertical" size={0} style={{ width: '100%', padding: '22px 20px 18px 20px' }}>
                      <div className={styles.planHeader}>
                        <Space align="center" size={12}>
                          {(() => {
                            const IconComp = plan.displayIcon ? PLAN_ICON_MAP[plan.displayIcon] : undefined;
                            const FinalIcon = IconComp || CrownOutlined;
                            return (
                              <div
                                style={{
                                  background: isSelected ? token.colorPrimary : token.colorFillTertiary,
                                  borderRadius: 12,
                                  padding: 8,
                                  display: 'flex',
                                }}
                              >
                                <FinalIcon style={{ fontSize: 22, color: isSelected ? '#fff' : token.colorPrimary }} />
                              </div>
                            );
                          })()}
                          <div>
                            <Title level={4} style={{ margin: 0, fontSize: 18, fontWeight: 900 }}>
                              {plan.planName}
                            </Title>
                            <Text type="secondary" style={{ fontSize: 13 }}>
                              {getPlanSubtitle(plan)}
                            </Text>
                          </div>
                        </Space>
                      </div>

                      <div className={styles.priceContainer}>
                        <div className={styles.price}>
                          {isFreePrice(plan.price) ? (
                            <span style={{ fontSize: 34, fontWeight: 900 }}>{t('pages.payment.plans.free', 'Free')}</span>
                          ) : (
                            <>
                              <span className={styles.currency}>¥</span>
                              {formatPriceValue(plan.price)}
                            </>
                          )}
                          {getBillingCycleLabel(plan.billingCycle) && (
                            <span className={styles.cycle}>/ {getBillingCycleLabel(plan.billingCycle)}</span>
                          )}
                        </div>
                        {getDisplayBadge(plan) && (
                          <Tag
                            color={normalizeCycleKey(plan.billingCycle) === 'TWO_WEEK' ? 'volcano' : 'green'}
                            style={{ borderRadius: 999, marginTop: 8 }}
                          >
                            {getDisplayBadge(plan)}
                          </Tag>
                        )}
                      </div>

                      {(plan.description || '') && !String(plan.displaySubtitle || '').trim() && (
                        <div style={{ marginBottom: 12, minHeight: 40 }}>
                          <Text type="secondary" style={{ fontSize: 13 }}>
                            {plan.description}
                          </Text>
                        </div>
                      )}

                      <div style={{ flex: 1, marginBottom: 14 }}>
                        {getPlanFeatures(plan).map((feature, index) => (
                          <div className={styles.featureItem} key={`${plan.id}-feature-${index}`}>
                            <CheckCircleFilled className={styles.checkIcon} />
                            <Text>{feature}</Text>
                          </div>
                        ))}
                        {Number.isFinite(purchaseLimit) && purchaseLimit > 0 && (
                          <div className={styles.featureItem}>
                            <InfoCircleFilled style={{ color: token.colorWarning, fontSize: 18, marginTop: 2 }} />
                            <Text type="warning">{t('pages.payment.plans.purchaseLimit', 'Purchase limit: {count} per user', { count: purchaseLimit })}</Text>
                          </div>
                        )}
                      </div>

                      {(() => {
                        return (
                          <Button
                            type={isSelected ? 'primary' : 'default'}
                            block
                            className={styles.subscribeButton}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedPlanCode(plan.planCode);
                              handleSubscribe(plan);
                            }}
                            disabled={actionDisabled}
                          >
                            {actionLabel}
                          </Button>
                        );
                      })()}
                    </Space>
                  </Card>
                );
              })}
            </div>
          )}

          {selectedPlan && !selectedPlanIsFree && (
            <div className={styles.stickyBar}>
              <div className={styles.stickyBarInner}>
                <div style={{ minWidth: 0 }}>
                  <div className={styles.stickyTitle}>
                    {t('pages.payment.plans.sticky.selected', 'Selected “{name}”', { name: selectedPlan.planName })}{' '}
                    <span style={{ fontWeight: 700, color: token.colorTextSecondary }}>
                      {t('pages.payment.plans.sticky.duration', 'Valid for {days} days', { days: selectedPlan.durationDays })}
                    </span>
                  </div>
                  <div className={styles.stickyMeta}>
                    {t('pages.payment.plans.sticky.meta', '{price}, includes {credits} credits', { price: isFreePrice(selectedPlan.price) ? t('pages.payment.plans.free', 'Free') : `¥${formatPriceValue(selectedPlan.price)}`, credits: formatQuota(selectedPlan.creditQuota) })}
                  </div>
                </div>
                <Button
                  type="primary"
                  size="large"
                  className={styles.subscribeButton}
                  onClick={() => handleSubscribe(selectedPlan)}
                  disabled={Boolean(selectedPlan.actionDisabled)}
                >
                  {selectedPlan.actionLabel || (isFreePrice(selectedPlan.price) ? t('pages.payment.plans.cta.tryNow', 'Try Now') : t('pages.payment.plans.cta.subscribeNow', 'Subscribe Now'))}
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );

  if (embedded) {
    return content;
  }

  return (
    <PageContainer
      className={styles.page}
      ghost={false}
      title={t('pages.payment.plans.title', 'Choose a subscription plan')}
      subTitle={t('pages.payment.plans.pageSubtitle', 'Compare plan benefits and choose the subscription that matches your current usage scale.')}
    >
      {content}
    </PageContainer>
  );
};

export default Plans;
